<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import DriveAPI from 'dashboard/api/drive';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FilePreviewModal from './components/FilePreviewModal.vue';
import FileTypeIcon from './components/FileTypeIcon.vue';
import AnimatedFolder from './components/AnimatedFolder.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const files = ref([]);
const allFiles = ref([]);
const loading = ref(false);
const isDragging = ref(false);
const searchQuery = ref('');
const currentFolder = ref('/');
const viewMode = ref('grid'); // 'grid' ou 'list'
const sortBy = ref('updated_at');
const sortOrder = ref('desc');
const fileTypeFilter = ref('all');
const showSortMenu = ref(false);
const showFilterMenu = ref(false);
const fileInput = ref(null);
const selectedFile = ref(null);
const selectedFileIndex = ref(0);
const showPreviewModal = ref(false);
const openMenuId = ref(null);
const showRenameModal = ref(false);
const fileToRename = ref(null);
const newFileName = ref('');
const renameDialog = ref(null);
const showMoveModal = ref(false);
const fileToMove = ref(null);
const newFolderPath = ref('');
const moveDialog = ref(null);
const showDeleteModal = ref(false);
const fileToDelete = ref(null);
const deleteDialog = ref(null);
const showCreateFolderModal = ref(false);
const newFolderName = ref('');
const createFolderDialog = ref(null);

// Récupérer les dossiers uniques depuis tous les fichiers
const folders = computed(() => {
  const folderSet = new Set();

  allFiles.value.forEach(file => {
    if (!file.folder_path) return;

    // Extraire les dossiers de niveau immédiat
    if (currentFolder.value === '/') {
      // À la racine, prendre le premier niveau
      const match = file.folder_path.match(/^\/([^/]+)/);
      if (match && match[1]) {
        folderSet.add(`/${match[1]}`);
      }
    } else if (file.folder_path.startsWith(currentFolder.value + '/')) {
      // Dans un dossier, chercher les sous-dossiers
      const relativePath = file.folder_path.substring(
        currentFolder.value.length + 1
      );
      const match = relativePath.match(/^([^/]+)/);
      if (match && match[1]) {
        folderSet.add(`${currentFolder.value}/${match[1]}`);
      }
    }
  });

  return Array.from(folderSet).sort();
});

const filteredFiles = computed(() => {
  let filtered = files.value;

  // Filtre par recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      file =>
        file.name.toLowerCase().includes(query) ||
        file.description?.toLowerCase().includes(query)
    );
  }

  // Filtre par type
  if (fileTypeFilter.value !== 'all') {
    filtered = filtered.filter(file => file.file_type === fileTypeFilter.value);
  }

  // Tri
  filtered = [...filtered].sort((a, b) => {
    let comparison = 0;

    switch (sortBy.value) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'size':
        comparison = (a.file_size || 0) - (b.file_size || 0);
        break;
      case 'type':
        comparison = (a.file_type || '').localeCompare(b.file_type || '');
        break;
      case 'updated_at':
      default:
        comparison = new Date(a.updated_at) - new Date(b.updated_at);
        break;
    }

    return sortOrder.value === 'asc' ? comparison : -comparison;
  });

  return filtered;
});

const breadcrumbs = computed(() => {
  const crumbs = [{ name: 'Home', path: '/', isHome: true }];

  if (currentFolder.value === '/') {
    crumbs.push({ name: 'Drive', path: '/' });
    return crumbs;
  }

  crumbs.push({ name: 'Drive', path: '/' });

  const parts = currentFolder.value.split('/').filter(Boolean);
  let currentPath = '';
  parts.forEach(part => {
    currentPath += `/${part}`;
    crumbs.push({
      name: part.charAt(0).toUpperCase() + part.slice(1),
      path: currentPath,
    });
  });

  return crumbs;
});

const isEmpty = computed(
  () => !loading.value && files.value.length === 0 && folders.value.length === 0
);

const fetchFiles = async () => {
  loading.value = true;
  try {
    // Récupérer tous les fichiers pour détecter les dossiers
    const allResponse = await DriveAPI.getAll({
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
    });
    allFiles.value = allResponse.data;

    // Filtrer les fichiers du dossier actuel (en excluant les .folder placeholders)
    files.value = allFiles.value.filter(
      file =>
        file.folder_path === currentFolder.value && file.name !== '.folder'
    );
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

const navigateToFolder = folderPath => {
  currentFolder.value = folderPath;
  fetchFiles();
};

const openFolder = folderPath => {
  navigateToFolder(folderPath);
};

const toggleMenu = (fileId, event) => {
  event?.stopPropagation();
  openMenuId.value = openMenuId.value === fileId ? null : fileId;
};

const closeMenu = () => {
  openMenuId.value = null;
};

const openRenameModal = async file => {
  fileToRename.value = file;
  newFileName.value = file.name;
  showRenameModal.value = true;
  await nextTick();
  renameDialog.value?.open();
};

const closeRenameModal = () => {
  showRenameModal.value = false;
  fileToRename.value = null;
  newFileName.value = '';
};

const renameFile = async () => {
  if (!newFileName.value || newFileName.value === fileToRename.value.name) {
    closeRenameModal();
    return;
  }

  try {
    await DriveAPI.update(fileToRename.value.id, {
      drive_file: { name: newFileName.value },
    });
    useAlert(t('DRIVE.API.SUCCESS.FILE_RENAMED'));
    await fetchFiles();
    closeRenameModal();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.RENAME_FAILED'));
  }
};

const openMoveModal = async file => {
  fileToMove.value = file;
  newFolderPath.value = file.folder_path;
  showMoveModal.value = true;
  await nextTick();
  moveDialog.value?.open();
};

const closeMoveModal = () => {
  showMoveModal.value = false;
  fileToMove.value = null;
  newFolderPath.value = '';
};

const moveFile = async () => {
  if (
    !newFolderPath.value ||
    newFolderPath.value === fileToMove.value.folder_path
  ) {
    closeMoveModal();
    return;
  }

  try {
    await DriveAPI.update(fileToMove.value.id, {
      drive_file: { folder_path: newFolderPath.value },
    });
    useAlert(t('DRIVE.API.SUCCESS.FILE_MOVED'));
    await fetchFiles();
    closeMoveModal();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.MOVE_FAILED'));
  }
};

const generateShareLink = async file => {
  try {
    const response = await DriveAPI.generateShareLink(file.id);
    const shareUrl = response.data.share_url;

    // Copier dans le presse-papiers
    await navigator.clipboard.writeText(shareUrl);
    useAlert(t('DRIVE.API.SUCCESS.SHARE_LINK_COPIED'));

    // Mettre à jour le fichier dans la liste
    await fetchFiles();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.SHARE_LINK_FAILED'));
  }
};

const disableShareLink = async file => {
  try {
    await DriveAPI.disableShareLink(file.id);
    useAlert(t('DRIVE.API.SUCCESS.SHARE_LINK_DISABLED'));

    // Mettre à jour le fichier dans la liste
    await fetchFiles();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.SHARE_LINK_DISABLE_FAILED'));
  }
};

const shareFile = async file => {
  if (file.share_enabled && file.share_url) {
    await navigator.clipboard.writeText(file.share_url);
    useAlert(t('DRIVE.API.SUCCESS.SHARE_LINK_COPIED'));
  } else {
    await generateShareLink(file);
  }
};

const setSortBy = (value, order = 'desc') => {
  sortBy.value = value;
  sortOrder.value = order;
  showSortMenu.value = false;
};

const setFileTypeFilter = value => {
  fileTypeFilter.value = value;
  showFilterMenu.value = false;
};

const toggleSortMenu = event => {
  event?.stopPropagation();
  showSortMenu.value = !showSortMenu.value;
  showFilterMenu.value = false;
};

const toggleFilterMenu = event => {
  event?.stopPropagation();
  showFilterMenu.value = !showFilterMenu.value;
  showSortMenu.value = false;
};

const closeAllMenus = () => {
  showSortMenu.value = false;
  showFilterMenu.value = false;
  closeMenu();
};

onMounted(() => {
  fetchFiles();
  document.addEventListener('click', closeAllMenus);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeAllMenus);
});

const dragCounter = ref(0);
const draggedItem = ref(null);
const dropTarget = ref(null);

const handleDragOver = e => {
  e.preventDefault();
  e.stopPropagation();
  // Seulement activer l'overlay si on drag des fichiers externes (pas des items internes)
  if (!draggedItem.value && !isDragging.value) {
    isDragging.value = true;
  }
};

const handleDragEnter = e => {
  e.preventDefault();
  e.stopPropagation();
  // Ne pas activer l'overlay pour les items internes
  if (!draggedItem.value) {
    dragCounter.value += 1;
    isDragging.value = true;
  }
};

const handleDragLeave = e => {
  e.preventDefault();
  e.stopPropagation();
  dragCounter.value -= 1;
  if (dragCounter.value === 0) {
    isDragging.value = false;
  }
};

const uploadFiles = async filesToUpload => {
  const uploadPromises = filesToUpload.map(async file => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('drive_file[name]', file.name);
      formData.append('drive_file[folder_path]', currentFolder.value);

      await DriveAPI.create(formData);
      useAlert(t('DRIVE.API.SUCCESS.FILE_UPLOADED', { name: file.name }));
    } catch (error) {
      useAlert(t('DRIVE.API.ERROR.UPLOAD_FAILED', { name: file.name }));
    }
  });

  await Promise.all(uploadPromises);
  await fetchFiles();
};

const handleDrop = async e => {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = false;
  dragCounter.value = 0;

  // Si c'est un item interne qu'on drag, ne rien faire (géré par handleFolderDrop)
  if (draggedItem.value) {
    draggedItem.value = null;
    dropTarget.value = null;
    return;
  }

  const droppedFiles = Array.from(e.dataTransfer.files);
  await uploadFiles(droppedFiles);
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileInput = async e => {
  const selectedFiles = Array.from(e.target.files);
  await uploadFiles(selectedFiles);
  e.target.value = '';
};

const openPreview = file => {
  const index = filteredFiles.value.findIndex(f => f.id === file.id);
  selectedFileIndex.value = index;
  selectedFile.value = file;
  showPreviewModal.value = true;
};

const closePreview = () => {
  showPreviewModal.value = false;
  selectedFile.value = null;
  selectedFileIndex.value = 0;
};

const goToPrevFile = () => {
  if (selectedFileIndex.value > 0) {
    selectedFileIndex.value -= 1;
    selectedFile.value = filteredFiles.value[selectedFileIndex.value];
  }
};

const goToNextFile = () => {
  if (selectedFileIndex.value < filteredFiles.value.length - 1) {
    selectedFileIndex.value += 1;
    selectedFile.value = filteredFiles.value[selectedFileIndex.value];
  }
};

const downloadFile = async file => {
  try {
    // La réponse peut avoir file_url, share_url ou url
    const fileUrl = file.file_url || file.share_url || file.url;
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      const response = await DriveAPI.download(file.id);
      window.open(response.data.download_url, '_blank');
    }
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.DOWNLOAD_FAILED'));
  }
};

const openDeleteModal = async file => {
  fileToDelete.value = file;
  showDeleteModal.value = true;
  await nextTick();
  deleteDialog.value?.open();
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  fileToDelete.value = null;
};

const deleteFile = async () => {
  try {
    await DriveAPI.delete(fileToDelete.value.id);
    useAlert(t('DRIVE.API.SUCCESS.FILE_DELETED'));
    closePreview();
    await fetchFiles();
    closeDeleteModal();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.DELETE_FAILED'));
  }
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

// Drag and drop pour déplacer fichiers/dossiers
const handleItemDragStart = (item, type) => {
  draggedItem.value = { item, type }; // type: 'file' ou 'folder'
};

const handleItemDragEnd = () => {
  draggedItem.value = null;
  dropTarget.value = null;
};

const handleFolderDragOver = (e, folderPath) => {
  if (!draggedItem.value) return;
  e.preventDefault();
  e.stopPropagation();
  dropTarget.value = folderPath;
};

const handleFolderDragLeave = () => {
  dropTarget.value = null;
};

const handleFolderDrop = async (e, targetFolderPath) => {
  e.preventDefault();
  e.stopPropagation();

  if (!draggedItem.value) return;

  const { item, type } = draggedItem.value;

  // Ne pas déplacer un dossier dans lui-même ou ses sous-dossiers
  if (type === 'folder') {
    const folderName = item.split('/').filter(Boolean).pop();
    const wouldBeNewPath =
      targetFolderPath === '/'
        ? `/${folderName}`
        : `${targetFolderPath}/${folderName}`;

    // Vérifier si on essaie de déplacer dans soi-même ou un sous-dossier
    if (
      item === targetFolderPath ||
      targetFolderPath.startsWith(item + '/') ||
      wouldBeNewPath === item
    ) {
      useAlert(t('DRIVE.API.ERROR.CANNOT_MOVE_FOLDER_INTO_ITSELF'));
      draggedItem.value = null;
      dropTarget.value = null;
      return;
    }
  }

  // Déplacer un fichier
  if (type === 'file') {
    try {
      await DriveAPI.update(item.id, {
        drive_file: { folder_path: targetFolderPath },
      });
      useAlert(t('DRIVE.API.SUCCESS.FILE_MOVED'));
      await fetchFiles();
    } catch (error) {
      useAlert(t('DRIVE.API.ERROR.MOVE_FAILED'));
    }
  }

  // Déplacer un dossier (déplacer tous les fichiers du dossier)
  if (type === 'folder') {
    try {
      // Trouver tous les fichiers dans le dossier source et ses sous-dossiers
      const filesToMove = allFiles.value.filter(
        f =>
          f.folder_path &&
          (f.folder_path === item || f.folder_path.startsWith(item + '/'))
      );

      // Calculer le nouveau nom du dossier (dernier segment)
      const folderName = item.split('/').filter(Boolean).pop();
      const newBasePath =
        targetFolderPath === '/'
          ? `/${folderName}`
          : `${targetFolderPath}/${folderName}`;

      // Déplacer chaque fichier
      await Promise.all(
        filesToMove.map(file => {
          let newPath;
          if (file.folder_path === item) {
            // Fichier directement dans le dossier source
            newPath = newBasePath;
          } else {
            // Fichier dans un sous-dossier
            const relativePath = file.folder_path.substring(item.length);
            newPath = newBasePath + relativePath;
          }
          return DriveAPI.update(file.id, {
            drive_file: { folder_path: newPath },
          });
        })
      );

      useAlert(t('DRIVE.API.SUCCESS.FOLDER_MOVED'));
      await fetchFiles();
    } catch (error) {
      useAlert(t('DRIVE.API.ERROR.MOVE_FAILED'));
    }
  }

  draggedItem.value = null;
  dropTarget.value = null;
};

const openCreateFolderModal = async () => {
  newFolderName.value = '';
  showCreateFolderModal.value = true;
  await nextTick();
  createFolderDialog.value?.open();
};

const closeCreateFolderModal = () => {
  showCreateFolderModal.value = false;
  newFolderName.value = '';
};

const createFolder = async () => {
  if (!newFolderName.value) {
    closeCreateFolderModal();
    return;
  }

  // Pour créer un dossier, on crée un fichier placeholder dans ce dossier
  const folderPath =
    currentFolder.value === '/'
      ? `/${newFolderName.value}`
      : `${currentFolder.value}/${newFolderName.value}`;

  try {
    // On crée un fichier .folder placeholder
    const formData = new FormData();
    const placeholderBlob = new Blob([''], { type: 'text/plain' });
    formData.append('file', placeholderBlob, '.folder');
    formData.append('drive_file[name]', '.folder');
    formData.append('drive_file[folder_path]', folderPath);

    await DriveAPI.create(formData);
    useAlert(
      t('DRIVE.API.SUCCESS.FOLDER_CREATED', { name: newFolderName.value })
    );
    await fetchFiles();
    closeCreateFolderModal();
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.FOLDER_CREATE_FAILED'));
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header class="sticky top-0 z-10">
      <div
        class="flex items-start sm:items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <span class="text-xl font-medium truncate text-n-slate-12">
          {{ t('DRIVE.TITLE') }}
        </span>

        <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
          <!-- Recherche -->
          <div class="flex items-center gap-2 w-full">
            <Input
              :model-value="searchQuery"
              type="search"
              :placeholder="t('DRIVE.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-full"
              @input="searchQuery = $event.target.value"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>

          <div class="flex items-center flex-shrink-0 gap-4">
            <!-- Sort & Filter -->
            <div class="flex items-center gap-2">
              <!-- Sort button -->
              <div class="relative">
                <Button
                  icon="i-lucide-arrow-up-down"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': showSortMenu }"
                  @click="toggleSortMenu"
                />
                <!-- Sort menu -->
                <div
                  v-if="showSortMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-50"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{ 'bg-n-brand text-white': sortBy === 'name' }"
                    @click="setSortBy('name', 'asc')"
                  >
                    <span class="i-lucide-text size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.SORT.NAME')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': sortBy === 'updated_at',
                    }"
                    @click="setSortBy('updated_at', 'desc')"
                  >
                    <span class="i-lucide-clock size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.SORT.DATE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{ 'bg-n-brand text-white': sortBy === 'size' }"
                    @click="setSortBy('size', 'desc')"
                  >
                    <span class="i-lucide-hard-drive size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.SORT.SIZE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{ 'bg-n-brand text-white': sortBy === 'type' }"
                    @click="setSortBy('type', 'asc')"
                  >
                    <span class="i-lucide-file-type size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.SORT.TYPE')
                    }}</span>
                  </button>
                </div>
              </div>

              <!-- Filter button -->
              <div class="relative">
                <Button
                  icon="i-lucide-filter"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-n-blue-3 text-n-blue-11':
                      showFilterMenu || fileTypeFilter !== 'all',
                  }"
                  @click="toggleFilterMenu"
                />
                <!-- Filter menu -->
                <div
                  v-if="showFilterMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-50"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'all',
                    }"
                    @click="setFileTypeFilter('all')"
                  >
                    <span class="i-lucide-files size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.ALL')
                    }}</span>
                  </button>
                  <hr class="m-1 rounded border-b border-n-weak" />
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'image',
                    }"
                    @click="setFileTypeFilter('image')"
                  >
                    <span class="i-lucide-image size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.IMAGES')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'document',
                    }"
                    @click="setFileTypeFilter('document')"
                  >
                    <span class="i-lucide-file-text size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.DOCUMENTS')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'video',
                    }"
                    @click="setFileTypeFilter('video')"
                  >
                    <span class="i-lucide-video size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.VIDEOS')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'audio',
                    }"
                    @click="setFileTypeFilter('audio')"
                  >
                    <span class="i-lucide-music size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.AUDIO')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    :class="{
                      'bg-n-brand text-white': fileTypeFilter === 'other',
                    }"
                    @click="setFileTypeFilter('other')"
                  >
                    <span class="i-lucide-file size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.FILTER.OTHER')
                    }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="w-px h-4 bg-n-strong" />

            <div class="flex items-center gap-2">
              <!-- Toggle view -->
              <div class="relative">
                <Button
                  icon="i-lucide-list"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': viewMode === 'list' }"
                  @click="viewMode = 'list'"
                />
              </div>
              <div class="relative">
                <Button
                  icon="i-lucide-layout-grid"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{ 'bg-n-blue-3 text-n-blue-11': viewMode === 'grid' }"
                  @click="viewMode = 'grid'"
                />
              </div>
            </div>

            <div class="w-px h-4 bg-n-strong" />

            <!-- Upload button -->
            <Button
              :label="t('DRIVE.UPLOAD')"
              icon="i-lucide-upload"
              size="sm"
              @click="triggerFileInput"
            />
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="handleFileInput"
            />
          </div>
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main
      class="flex-1 px-6 overflow-y-auto relative"
      @dragenter="handleDragEnter"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <!-- Overlay de dropzone global -->
      <div
        v-if="isDragging"
        class="absolute inset-0 z-40 bg-n-blue-2/80 dark:bg-n-blue-3/80 backdrop-blur-sm border-4 border-dashed border-n-blue-9 rounded-lg flex items-center justify-center pointer-events-none"
      >
        <div class="flex flex-col items-center gap-4 text-n-blue-11">
          <span class="i-lucide-cloud-upload size-16" />
          <p class="text-xl font-semibold">
            {{ t('DRIVE.DROP_FILES_HERE') }}
          </p>
        </div>
      </div>

      <div class="w-full max-w-[60rem] h-full mx-auto py-4">
        <!-- Breadcrumb navigation (toujours visible, même pendant le loading) -->
        <nav class="mb-2 px-6" :aria-label="t('DRIVE.BREADCRUMB')">
          <div
            class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-white dark:bg-white/10 border border-n-weak"
          >
            <ol class="flex flex-wrap items-center gap-1.5 text-sm">
              <li
                v-for="(crumb, index) in breadcrumbs"
                :key="crumb.path + index"
                class="inline-flex items-center gap-1.5"
              >
                <button
                  v-if="crumb.isHome"
                  class="inline-flex items-center px-1 py-0.5 rounded transition-colors text-n-slate-11 hover:text-n-slate-12"
                  :class="{
                    'bg-n-brand/10 ring-1 ring-n-brand':
                      dropTarget === crumb.path && draggedItem,
                  }"
                  @click="navigateToFolder(crumb.path)"
                  @dragover="handleFolderDragOver($event, crumb.path)"
                  @dragleave="handleFolderDragLeave"
                  @drop="handleFolderDrop($event, crumb.path)"
                >
                  <span class="i-lucide-home size-4" />
                </button>
                <button
                  v-else
                  class="px-1 py-0.5 rounded transition-colors hover:text-n-slate-12"
                  :class="[
                    index === breadcrumbs.length - 1
                      ? 'text-n-slate-12 font-medium cursor-default'
                      : 'text-n-slate-11',
                    {
                      'bg-n-brand/10 ring-1 ring-n-brand':
                        dropTarget === crumb.path && draggedItem,
                    },
                  ]"
                  @click="navigateToFolder(crumb.path)"
                  @dragover="handleFolderDragOver($event, crumb.path)"
                  @dragleave="handleFolderDragLeave"
                  @drop="handleFolderDrop($event, crumb.path)"
                >
                  {{ crumb.name }}
                </button>
                <span
                  v-if="index < breadcrumbs.length - 1"
                  role="presentation"
                  aria-hidden="true"
                  class="i-lucide-chevron-right size-3.5 text-n-slate-10"
                />
              </li>
            </ol>
          </div>
        </nav>

        <!-- Loading (uniquement pour les fichiers/dossiers) -->
        <div
          v-if="loading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="isEmpty"
          class="flex flex-col items-center justify-center py-24 rounded-lg border-2 bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-dashed border-n-weak"
        >
          <div
            class="mb-6 w-20 h-20 rounded-full bg-n-blue-2 dark:bg-n-blue-3 flex items-center justify-center"
          >
            <span class="i-lucide-cloud-upload size-10 text-n-blue-10" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('DRIVE.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
            {{ t('DRIVE.EMPTY_STATE.DESCRIPTION') }}
          </p>
          <Button
            :label="t('DRIVE.UPLOAD')"
            icon="i-lucide-upload"
            size="sm"
            @click="triggerFileInput"
          />
        </div>

        <!-- Files content -->
        <div v-else>
          <!-- Grid view - Finder macOS style -->
          <div
            v-if="viewMode === 'grid'"
            class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 p-6"
          >
            <!-- Dossiers avec animation -->
            <div
              v-for="folder in folders"
              :key="folder"
              draggable="true"
              class="flex flex-col rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak group cursor-pointer animate-fade-in hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 transition-all duration-150"
              :class="{
                'ring-2 ring-n-brand ring-offset-2':
                  dropTarget === folder && draggedItem,
                'opacity-50':
                  draggedItem?.type === 'folder' &&
                  draggedItem?.item === folder,
              }"
              @click="openFolder(folder)"
              @dragstart="handleItemDragStart(folder, 'folder')"
              @dragend="handleItemDragEnd"
              @dragover="handleFolderDragOver($event, folder)"
              @dragleave="handleFolderDragLeave"
              @drop="handleFolderDrop($event, folder)"
            >
              <!-- Header dossier -->
              <div
                class="flex items-center gap-2 px-3 py-2 border-b border-n-weak"
              >
                <span class="i-lucide-folder size-4 text-n-blue-10" />
                <span
                  class="flex-1 text-sm font-normal text-n-slate-11 truncate"
                  :title="folder.split('/').pop()"
                >
                  {{ folder.split('/').pop() }}
                </span>
              </div>

              <!-- Icône dossier animé -->
              <div class="flex items-center justify-center py-4 px-4">
                <AnimatedFolder color="#4A90E2" :size="0.75" />
              </div>
            </div>

            <!-- Fichiers style Google Drive avec charte de l'app -->
            <div
              v-for="file in filteredFiles"
              :key="file.id"
              draggable="true"
              class="flex flex-col rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak group cursor-pointer relative animate-fade-in hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 transition-all duration-150"
              :class="{
                'opacity-50':
                  draggedItem?.type === 'file' &&
                  draggedItem?.item.id === file.id,
              }"
              @click="openPreview(file)"
              @dragstart="handleItemDragStart(file, 'file')"
              @dragend="handleItemDragEnd"
            >
              <!-- Header avec icône type + nom + menu -->
              <div
                class="flex items-center gap-2 px-3 py-2 border-b border-n-weak"
              >
                <!-- Petit icône de type -->
                <span
                  class="flex-shrink-0 size-4 text-n-slate-10"
                  :class="[file.icon_class]"
                />

                <!-- Nom du fichier -->
                <span
                  class="flex-1 text-sm font-normal text-n-slate-11 truncate"
                  :title="file.name"
                >
                  {{ file.name }}
                </span>

                <!-- Menu 3 points -->
                <div class="flex-shrink-0 relative">
                  <button
                    class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                    @click.stop="toggleMenu(file.id, $event)"
                  >
                    <span class="i-lucide-more-vertical size-4" />
                  </button>

                  <!-- Dropdown actions (style context menu) -->
                  <div
                    v-if="openMenuId === file.id"
                    class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-50"
                    @click.stop
                  >
                    <button
                      class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                      @click="
                        openRenameModal(file);
                        closeMenu();
                      "
                    >
                      <span class="i-lucide-pencil size-3.5 flex-shrink-0" />
                      <span class="mx-2 truncate flex-1 text-left">{{
                        t('DRIVE.RENAME')
                      }}</span>
                    </button>
                    <button
                      class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                      @click="
                        openMoveModal(file);
                        closeMenu();
                      "
                    >
                      <span
                        class="i-lucide-folder-input size-3.5 flex-shrink-0"
                      />
                      <span class="mx-2 truncate flex-1 text-left">{{
                        t('DRIVE.MOVE')
                      }}</span>
                    </button>
                    <button
                      class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                      @click="
                        shareFile(file);
                        closeMenu();
                      "
                    >
                      <span
                        class="flex-shrink-0 size-3.5"
                        :class="
                          file.share_enabled
                            ? 'i-lucide-check-circle'
                            : 'i-lucide-link'
                        "
                      />
                      <span class="mx-2 truncate flex-1 text-left">{{
                        t('DRIVE.SHARE')
                      }}</span>
                    </button>
                    <hr class="m-1 rounded border-b border-n-weak" />
                    <button
                      class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                      @click="
                        downloadFile(file);
                        closeMenu();
                      "
                    >
                      <span class="i-lucide-download size-3.5 flex-shrink-0" />
                      <span class="mx-2 truncate flex-1 text-left">{{
                        t('DRIVE.DOWNLOAD')
                      }}</span>
                    </button>
                    <hr class="m-1 rounded border-b border-n-weak" />
                    <button
                      class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                      @click="
                        openDeleteModal(file);
                        closeMenu();
                      "
                    >
                      <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
                      <span class="mx-2 truncate flex-1 text-left">{{
                        t('DRIVE.DELETE')
                      }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Icône/Miniature principale -->
              <div class="flex items-center justify-center py-4 px-4">
                <!-- Miniature pour images -->
                <div
                  v-if="file.file_type === 'image' && file.thumbnail_url"
                  class="w-full aspect-video rounded overflow-hidden"
                >
                  <img
                    :src="file.thumbnail_url"
                    :alt="file.name"
                    class="w-full h-full object-cover"
                  />
                </div>

                <!-- Icône pour autres fichiers -->
                <div v-else class="w-16 h-16">
                  <FileTypeIcon :file="file" size="md" />
                </div>
              </div>
            </div>

            <!-- Encart de création de dossier -->
            <div
              class="flex flex-col rounded-lg bg-white dark:bg-white/10 border-2 border-dashed border-n-weak group cursor-pointer animate-fade-in hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 transition-all duration-150"
              @click="openCreateFolderModal"
            >
              <!-- Header -->
              <div
                class="flex items-center gap-2 px-3 py-2 border-b border-n-weak"
              >
                <span class="i-lucide-folder-plus size-4 text-n-blue-10" />
                <span
                  class="flex-1 text-sm font-normal text-n-slate-11 truncate"
                >
                  {{ t('DRIVE.CREATE_FOLDER') }}
                </span>
              </div>

              <!-- Icône plus -->
              <div
                class="flex items-center justify-center py-4 px-4 flex-1 min-h-[100px]"
              >
                <span
                  class="i-lucide-plus size-16 text-n-blue-10 group-hover:text-n-blue-11 group-hover:scale-110 transition-all thin-stroke"
                />
              </div>
            </div>
          </div>

          <!-- List view avec même style que grid -->
          <div v-if="viewMode === 'list'" class="flex flex-col gap-2 p-6">
            <!-- Dossiers -->
            <div
              v-for="folder in folders"
              :key="folder"
              draggable="true"
              class="flex items-center gap-3 px-4 py-3 rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 cursor-pointer group transition-all duration-150"
              :class="{
                'ring-2 ring-n-brand ring-offset-2':
                  dropTarget === folder && draggedItem,
                'opacity-50':
                  draggedItem?.type === 'folder' &&
                  draggedItem?.item === folder,
              }"
              @click="openFolder(folder)"
              @dragstart="handleItemDragStart(folder, 'folder')"
              @dragend="handleItemDragEnd"
              @dragover="handleFolderDragOver($event, folder)"
              @dragleave="handleFolderDragLeave"
              @drop="handleFolderDrop($event, folder)"
            >
              <span
                class="i-lucide-folder size-5 text-n-blue-10 flex-shrink-0"
              />
              <span class="flex-1 text-sm font-normal text-n-slate-11 truncate">
                {{ folder.split('/').pop() }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ t('DRIVE.FOLDER') }}
              </span>
            </div>

            <!-- Fichiers -->
            <div
              v-for="file in filteredFiles"
              :key="file.id"
              draggable="true"
              class="flex items-center gap-3 px-4 py-3 rounded-lg bg-n-alpha-1 dark:bg-n-alpha-2 border border-n-weak hover:bg-n-blue-2 dark:hover:bg-n-blue-3 hover:border-n-blue-6 cursor-pointer group relative transition-all duration-150"
              :class="{
                'opacity-50':
                  draggedItem?.type === 'file' &&
                  draggedItem?.item.id === file.id,
              }"
              @click="openPreview(file)"
              @dragstart="handleItemDragStart(file, 'file')"
              @dragend="handleItemDragEnd"
            >
              <!-- Icône/Miniature -->
              <div class="flex-shrink-0 w-10 h-10">
                <!-- Miniature pour images -->
                <div
                  v-if="file.file_type === 'image' && file.thumbnail_url"
                  class="w-full h-full rounded overflow-hidden"
                >
                  <img
                    :src="file.thumbnail_url"
                    :alt="file.name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <!-- Icône pour autres fichiers -->
                <FileTypeIcon v-else :file="file" size="sm" />
              </div>

              <!-- Nom -->
              <span class="flex-1 text-sm font-normal text-n-slate-11 truncate">
                {{ file.name }}
              </span>

              <!-- Taille -->
              <span class="text-sm text-n-slate-10 w-20 text-right">
                {{ file.human_file_size }}
              </span>

              <!-- Date -->
              <span
                class="text-sm text-n-slate-10 w-32 text-right hidden md:block"
              >
                {{ formatDate(file.updated_at) }}
              </span>

              <!-- Menu 3 points -->
              <div class="flex-shrink-0 relative">
                <button
                  class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  @click.stop="toggleMenu(file.id, $event)"
                >
                  <span class="i-lucide-more-vertical size-4" />
                </button>

                <!-- Dropdown actions (style context menu) -->
                <div
                  v-if="openMenuId === file.id"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50 min-w-[200px] z-50"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      openRenameModal(file);
                      closeMenu();
                    "
                  >
                    <span class="i-lucide-pencil size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.RENAME')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      openMoveModal(file);
                      closeMenu();
                    "
                  >
                    <span
                      class="i-lucide-folder-input size-3.5 flex-shrink-0"
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.MOVE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      shareFile(file);
                      closeMenu();
                    "
                  >
                    <span
                      class="flex-shrink-0 size-3.5"
                      :class="
                        file.share_enabled
                          ? 'i-lucide-check-circle'
                          : 'i-lucide-link'
                      "
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.SHARE')
                    }}</span>
                  </button>
                  <hr class="m-1 rounded border-b border-n-weak" />
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      downloadFile(file);
                      closeMenu();
                    "
                  >
                    <span class="i-lucide-download size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.DOWNLOAD')
                    }}</span>
                  </button>
                  <hr class="m-1 rounded border-b border-n-weak" />
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-n-slate-12 hover:bg-n-brand hover:text-white cursor-pointer"
                    @click="
                      openDeleteModal(file);
                      closeMenu();
                    "
                  >
                    <span class="i-lucide-trash-2 size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('DRIVE.DELETE')
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- File input (caché) -->
      <input
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        @change="handleFileInput"
      />

      <!-- Preview modal -->
      <FilePreviewModal
        :file="selectedFile"
        :is-open="showPreviewModal"
        :current-index="selectedFileIndex"
        :total-files="filteredFiles.length"
        @close="closePreview"
        @download="downloadFile"
        @delete="openDeleteModal"
        @rename="openRenameModal"
        @move="openMoveModal"
        @share="shareFile"
        @generate-share="generateShareLink"
        @disable-share="disableShareLink"
        @prev="goToPrevFile"
        @next="goToNextFile"
      />

      <!-- Rename modal -->
      <Dialog
        v-if="showRenameModal"
        ref="renameDialog"
        type="edit"
        width="md"
        :title="t('DRIVE.RENAME_MODAL.TITLE')"
        :description="t('DRIVE.RENAME_MODAL.DESCRIPTION')"
        :confirm-button-label="t('DRIVE.RENAME_MODAL.CONFIRM')"
        @confirm="renameFile"
        @close="closeRenameModal"
      >
        <Input
          v-model="newFileName"
          :label="t('DRIVE.RENAME_MODAL.LABEL')"
          :placeholder="t('DRIVE.RENAME_MODAL.PLACEHOLDER')"
          @keyup.enter="renameFile"
        />
      </Dialog>

      <!-- Move modal -->
      <Dialog
        v-if="showMoveModal"
        ref="moveDialog"
        type="edit"
        width="md"
        :title="t('DRIVE.MOVE_MODAL.TITLE')"
        :description="t('DRIVE.MOVE_MODAL.DESCRIPTION')"
        :confirm-button-label="t('DRIVE.MOVE_MODAL.CONFIRM')"
        @confirm="moveFile"
        @close="closeMoveModal"
      >
        <Input
          v-model="newFolderPath"
          :label="t('DRIVE.MOVE_MODAL.LABEL')"
          :placeholder="t('DRIVE.MOVE_MODAL.PLACEHOLDER')"
          @keyup.enter="moveFile"
        />
      </Dialog>

      <!-- Delete confirmation modal -->
      <Dialog
        v-if="showDeleteModal"
        ref="deleteDialog"
        type="alert"
        width="md"
        :title="t('DRIVE.DELETE_MODAL.TITLE')"
        :description="
          t('DRIVE.DELETE_MODAL.DESCRIPTION', { name: fileToDelete?.name })
        "
        :confirm-button-label="t('DRIVE.DELETE_MODAL.CONFIRM')"
        @confirm="deleteFile"
        @close="closeDeleteModal"
      />

      <!-- Create folder modal -->
      <Dialog
        v-if="showCreateFolderModal"
        ref="createFolderDialog"
        type="edit"
        width="md"
        :title="t('DRIVE.CREATE_FOLDER_MODAL.TITLE')"
        :description="t('DRIVE.CREATE_FOLDER_MODAL.DESCRIPTION')"
        :confirm-button-label="t('DRIVE.CREATE_FOLDER_MODAL.CONFIRM')"
        @confirm="createFolder"
        @close="closeCreateFolderModal"
      >
        <Input
          v-model="newFolderName"
          :label="t('DRIVE.CREATE_FOLDER_MODAL.LABEL')"
          :placeholder="t('DRIVE.CREATE_FOLDER_MODAL.PLACEHOLDER')"
          @keyup.enter="createFolder"
        />
      </Dialog>
    </main>
  </section>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in {
  animation: fade-in 150ms ease-in-out;
}

.thin-stroke {
  stroke-width: 1.5;
}
</style>
